/**
 * @file Withdraw functionality for Nucleus vaults
 * @module vaults/withdraw
 */

import { type Address, parseUnits } from "viem";
import { getRateInQuoteAndSharesAndWantAssetDecimals } from "../../api/accountant";
import { getErc20Decimals } from "../../api/erc20";
import { getAssetSlippage } from "../../api/solver-slippage";
import { type ChainId, toChainId } from "../../api/vault-config";
import {
  ATOMIC_QUEUE_CONTRACT_ADDRESS,
  DEFAULT_APPROVAL_AMOUNT,
  DEFAULT_DEADLINE,
  DEFAULT_WITHDRAW_SLIPPAGE,
} from "../../constants";
import { AtomicQueueAbi } from "../../contracts/atomic-queue-abi";
import { BoringVaultAbi } from "../../contracts/boring-vault-abi";
import { tryCatch } from "../../utils/try-catch";
import {
  type BridgeTransactionData,
  prepareBridgeTransactionData,
} from "../bridge";
import type { VaultKey } from "../config";
import { getVaultByKey } from "../index";
import type { TokenKey } from "../tokens";
import { calculateAtomicPrice, prepareUserRequest } from "./utils";

/**
 * Arguments for an atomic request to the AtomicQueue contract
 * @typedef {Object} AtomicRequestArgs
 * @property {Address} offer - Address of the token being offered (shares token address)
 * @property {Address} want - Address of the token being requested (want token address)
 * @property {UserRequest} userRequest - User request details
 */
export type AtomicRequestArgs = {
  offer: Address; // sharesTokenAddress
  want: Address; // wantTokenAddress
  userRequest: UserRequest; // userRequest
};

/**
 * Options for an atomic request
 * @typedef {Object} AtomicRequestOptions
 * @property {Address} atomicQueueContractAddress - Address of the AtomicQueue contract
 * @property {number} chainId - ID of the destination chain
 */
export type AtomicRequestOptions = {
  atomicQueueContractAddress: Address; // atomicQueueContractAddress
  chainId: ChainId; // destinationChainId
};

/**
 * User request details for an atomic swap
 * @typedef {Object} UserRequest
 * @property {number} deadline - Unix timestamp when the request expires
 * @property {bigint} atomicPrice - Rate in quote with fee applied
 * @property {bigint} offerAmount - Amount of shares token to redeem
 * @property {boolean} inSolve - Whether the request is in solve mode (always false for new requests)
 */
export type UserRequest = {
  deadline: bigint; // deadline
  atomicPrice: bigint; // rateInQuoteWithFee
  offerAmount: bigint; // redeemAmount
  inSolve: boolean; // false
};

/**
 * Parameters for preparing a withdrawal transaction from a vault
 * @interface PrepareWithdrawTransactionDataParams
 * @property {ChainId} chainId - ID of the chain where the withdrawal will occur
 * @property {number} [deadline] - Unix timestamp when the request expires (optional)
 * @property {number} offerAmount - Amount of vault shares to withdraw
 * @property {number} [slippage] - Maximum acceptable slippage percentage (optional)
 * @property {Address} userAddress - Ethereum address of the user making the withdrawal
 * @property {VaultKey} vaultKey - Unique identifier for the vault
 * @property {TokenKey} wantTokenSymbol - Symbol of the token the user wants to receive
 */
interface PrepareWithdrawTransactionDataParams {
  chainId: ChainId;
  deadline?: number;
  offerAmount: string;
  slippage?: number;
  userAddress: Address;
  vaultKey: VaultKey;
  wantTokenSymbol: TokenKey;
}

/**
 * Result object containing transaction data for a withdrawal operation
 * @interface WithdrawTransactionData
 * @property {typeof AtomicQueueAbi} abi - ABI for the AtomicQueue contract
 * @property {Address} address - Address of the AtomicQueue contract
 * @property {'updateAtomicRequest'} functionName - Name of the function to call
 * @property {[Address, Address, UserRequest]} args - Arguments for the updateAtomicRequest function:
 *   [offer, want, userRequest]
 * @property {number} chainId - ID of the chain where the transaction should be executed
 */
interface WithdrawTransactionData {
  abi: typeof AtomicQueueAbi;
  address: Address;
  functionName: "updateAtomicRequest";
  args: [offer: Address, want: Address, userRequest: UserRequest];
  chainId: number;
}

/**
 * Prepares the transaction data needed to withdraw assets from a vault
 *
 * This function calculates the appropriate exchange rate with slippage protection
 * and prepares a user request for the AtomicQueue contract. It returns a transaction
 * object that can be used with viem or ethers.js to execute the withdrawal.
 *
 * @example
 * ```typescript
 * const withdrawData = await prepareWithdrawData({
 *   vaultKey: 'bobaeth',
 *   chainId: 1, // Ethereum mainnet
 *   userAddress: '0x1234...',
 *   wantAsset: TokenKey.WETH,
 *   redeemAmount: BigInt('1000000000000000000'), // 1 token
 *   deadline: Math.floor(Date.now() / 1000) + 3600, // 1 hour from now
 *   slippage: 0.01, // 1% slippage
 * });
 * ```
 *
 * @param {PrepareWithdrawTransactionParams} params - Parameters for the withdraw operation
 * @param {VaultKey} params.vaultKey - Unique identifier for the vault
 * @param {ChainId} params.chainId - ID of the chain where the withdrawal will occur
 * @param {Address} params.userAddress - Ethereum address of the user making the withdrawal
 * @param {TokenKey} params.wantAsset - Key of the asset the user wants to receive
 * @param {bigint} params.offerAmount - Amount of vault tokens to redeem (in base units)
 * @param {number} [params.deadline] - Unix timestamp when the request expires
 * @param {number} [params.slippage] - Maximum acceptable slippage in basis points (bps).
 *                                     e.g., 20 for 0.2%, 50 for 0.5%
 *
 * @returns {Promise<WithdrawTransactionData>} Promise resolving to the prepared transaction data
 * @throws {Error} If the vault key is invalid, if contracts are not properly configured,
 *                 or if the specified chain or token is not supported
 */
const prepareWithdrawTransactionData = async ({
  vaultKey,
  chainId,
  wantTokenSymbol,
  offerAmount,
  deadline = DEFAULT_DEADLINE,
  slippage,
}: PrepareWithdrawTransactionDataParams): Promise<WithdrawTransactionData> => {
  const vault = await getVaultByKey(vaultKey);
  const normalizedChainId = toChainId(chainId);
  if (!vault) {
    throw new Error(`Invalid vault key: ${vaultKey}`);
  }

  if (!vault.contracts) {
    throw new Error(`Contracts not configured for vault ${vaultKey}`);
  }

  // Check if contracts exist
  if (!vault.contracts.boringVault) {
    throw new Error(
      `BoringVault contract not configured for vault ${vaultKey}`
    );
  }
  const boringVaultAddress = vault.contracts.boringVault;

  if (!vault.contracts.accountant) {
    throw new Error(`Accountant contract not configured for vault ${vaultKey}`);
  }
  const accountantAddress = vault.contracts.accountant;

  // Updated chain validation
  const destinationChain =
    vault.withdraw.sourceChains[normalizedChainId].destinationChains[
      normalizedChainId
    ];
  const wantTokens = destinationChain.wantTokens;
  if (!!wantTokens && !wantTokens[wantTokenSymbol]) {
    throw new Error(
      `Invalid want token "${wantTokenSymbol}" for vault ${vaultKey}. ` +
        `Allowed tokens are: ${Object.keys(wantTokens).join(", ")}`
    );
  }

  const wantAssetAddress = wantTokens[wantTokenSymbol].address;
  if (!wantAssetAddress) {
    throw new Error(`Invalid want token symbol: ${wantTokenSymbol}`);
  }

  const [wantAssetDecimals, sharesAssetDecimals, rateInQuote] =
    await getRateInQuoteAndSharesAndWantAssetDecimals({
      sharesAssetAddress: boringVaultAddress,
      wantAssetAddress: wantAssetAddress,
      accountantAddress: accountantAddress,
      chainId: normalizedChainId,
    });

  if (wantAssetDecimals.status === "failure") {
    throw new Error(wantAssetDecimals.error.message);
  }

  if (rateInQuote.status === "failure") {
    throw new Error(rateInQuote.error.message);
  }

  if (sharesAssetDecimals.status === "failure") {
    throw new Error(sharesAssetDecimals.error.message);
  }

  const formattedOfferAmount = parseUnits(
    offerAmount,
    sharesAssetDecimals.result
  );

  const {
    data: solverSlippage,
    error: solverSlippageError,
    success: solverSlippageSuccess,
  } = await tryCatch(
    getAssetSlippage(normalizedChainId, boringVaultAddress, wantAssetAddress)
  );
  if (!solverSlippageSuccess) {
    throw new Error(
      `Error getting solver slippage: ${solverSlippageError.message}`
    );
  }
  const effectiveSlippage = slippage ?? solverSlippage;

  const atomicPrice = calculateAtomicPrice(
    rateInQuote.result,
    wantAssetDecimals.result,
    effectiveSlippage
  );

  const userRequest = prepareUserRequest(
    formattedOfferAmount,
    atomicPrice,
    deadline
  );

  return {
    abi: AtomicQueueAbi,
    address: ATOMIC_QUEUE_CONTRACT_ADDRESS,
    functionName: "updateAtomicRequest",
    args: [boringVaultAddress, wantAssetAddress, userRequest],
    chainId: normalizedChainId,
  };
};

/**
 * Parameters for preparing a cross-chain bridge and withdrawal transaction
 * @interface PrepareBridgeAndWithdrawTransactionDataParams
 * @extends {Omit<PrepareWithdrawTransactionDataParams, 'chainId'>}
 * @property {ChainId} sourceChainId - Chain ID where shares currently exist
 * @property {ChainId} destinationChainId - Chain ID where assets will be withdrawn
 * @property {number} offerAmount - Amount of vault shares to withdraw
 * @property {number} [deadline] - Unix timestamp when the request expires (optional)
 * @property {number} [slippage] - Maximum acceptable slippage percentage (optional)
 * @property {Address} userAddress - Ethereum address of the user making the withdrawal
 * @property {VaultKey} vaultKey - Unique identifier for the vault
 * @property {TokenKey} wantTokenSymbol - Symbol of the token the user wants to receive
 */
interface PrepareBridgeAndWithdrawTransactionDataParams
  extends Omit<PrepareWithdrawTransactionDataParams, "chainId"> {
  sourceChainId: ChainId;
  destinationChainId: ChainId;
}

/**
 * Combined result containing both bridge and withdrawal transaction data
 * @interface BridgeAndWithdrawResult
 */
interface BridgeAndWithdrawTransactionData {
  bridgeTransactionData: BridgeTransactionData; // Data for bridging shares to destination chain
  withdrawTransactionData: WithdrawTransactionData; // Data for withdraw request on destination chain
}

/**
 * Prepares transaction data for cross-chain withdrawals from a vault
 *
 * This function handles both:
 * 1. Bridging vault shares from source chain to destination chain
 * 2. Creating a withdraw request on the destination chain
 *
 * The withdraw request can be submitted before the bridge completes - the solver
 * will wait for bridged shares to arrive before processing the request.
 *
 * @example
 * ```typescript
 * const result = await prepareBridgeAndWithdrawData({
 *   vaultKey: 'bobaeth',
 *   sourceChainId: 42161, // Arbitrum
 *   destinationChainId: 1, // Ethereum
 *   userAddress: '0x1234...',
 *   wantTokenSymbol: TokenKey.ETH,
 *   offerAmount: 1.5, // 1.5 shares
 *   deadline: Math.floor(Date.now() / 1000) + 3600, // 1 hour
 *   slippage: 0.005 // 0.5%
 * });
 * ```
 *
 * @param {PrepareBridgeAndWithdrawParams} params - Parameters for the cross-chain withdraw
 * @returns {Promise<BridgeAndWithdrawResult>} Transaction data for both bridge and withdraw operations
 * @throws {Error} If vault configuration is invalid or if preparing either transaction fails
 */
const prepareBridgeAndWithdrawTransactionData = async ({
  deadline = DEFAULT_DEADLINE,
  destinationChainId,
  offerAmount,
  slippage = DEFAULT_WITHDRAW_SLIPPAGE,
  sourceChainId,
  userAddress,
  vaultKey,
  wantTokenSymbol,
}: PrepareBridgeAndWithdrawTransactionDataParams): Promise<BridgeAndWithdrawTransactionData> => {
  const vault = await getVaultByKey(vaultKey);
  if (!vault) {
    throw new Error(`Invalid vault key: ${vaultKey}`);
  }

  const {
    data: withdrawTransactionData,
    error: withdrawError,
    success: withdrawSuccess,
  } = await tryCatch(
    prepareWithdrawTransactionData({
      vaultKey,
      chainId: destinationChainId,
      userAddress,
      wantTokenSymbol,
      offerAmount,
      deadline,
      slippage,
    })
  );

  if (!withdrawSuccess) {
    throw new Error(withdrawError.message);
  }

  const { args } = withdrawTransactionData;
  const [, , userRequest] = args;
  const { offerAmount: bridgeAmount } = userRequest;

  const {
    data: bridgeTransactionData,
    error: bridgeError,
    success: bridgeSuccess,
  } = await tryCatch(
    prepareBridgeTransactionData({
      vaultKey,
      bridgeAmount: bridgeAmount,
      sourceChainId,
      destinationChainId,
      userAddress,
    })
  );

  if (!bridgeSuccess) {
    throw new Error(bridgeError.message);
  }

  return {
    bridgeTransactionData: bridgeTransactionData,
    withdrawTransactionData: withdrawTransactionData,
  };
};

interface PrepareApproveWithdrawTokenParams {
  vaultKey: VaultKey;
  withdrawAmount?: string;
  chainId: ChainId;
}

interface ApproveWithdrawTokenTransactionData {
  abi: typeof BoringVaultAbi;
  address: Address;
  functionName: "approve";
  args: [Address, bigint];
}

const prepareApproveWithdrawToken = async ({
  vaultKey,
  withdrawAmount,
  chainId,
}: PrepareApproveWithdrawTokenParams): Promise<ApproveWithdrawTokenTransactionData> => {
  const normalizedChainId = toChainId(chainId);
  const vault = await getVaultByKey(vaultKey);

  if (!vault) {
    throw new Error(`Invalid vault key: ${vaultKey}`);
  }

  if (!vault.contracts) {
    throw new Error(`Contracts not configured for vault ${vaultKey}`);
  }

  if (!vault.contracts.boringVault) {
    throw new Error(`Teller contract not configured for vault ${vaultKey}`);
  }
  const withdrawAssetAddress = vault.contracts.boringVault;

  const {
    data: withdrawAssetDecimals,
    error: withdrawAssetDecimalsError,
    success: withdrawAssetDecimalsSuccess,
  } = await tryCatch(
    getErc20Decimals({
      chainId: normalizedChainId,
      tokenAddress: withdrawAssetAddress,
    })
  );

  if (!withdrawAssetDecimalsSuccess) {
    throw new Error(
      `Error getting withdraw asset decimals. ${withdrawAssetDecimalsError.name}: ${withdrawAssetDecimalsError.message}`
    );
  }

  if (!withdrawAssetDecimals) {
    throw new Error(`Invalid withdraw token symbol: ${withdrawAssetAddress}`);
  }

  const withdrawAmountAsBitInt = withdrawAmount
    ? parseUnits(withdrawAmount, withdrawAssetDecimals)
    : DEFAULT_APPROVAL_AMOUNT;

  return {
    abi: BoringVaultAbi,
    address: withdrawAssetAddress,
    functionName: "approve",
    args: [ATOMIC_QUEUE_CONTRACT_ADDRESS, withdrawAmountAsBitInt],
  };
};

export {
  prepareApproveWithdrawToken,
  prepareBridgeAndWithdrawTransactionData,
  prepareWithdrawTransactionData,
};

export type {
  ApproveWithdrawTokenTransactionData,
  BridgeAndWithdrawTransactionData,
  PrepareBridgeAndWithdrawTransactionDataParams,
  PrepareWithdrawTransactionDataParams,
  WithdrawTransactionData,
};
